import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth, useGymId } from '@structural-climbing/auth'
import { Button, Spinner, useTabParam } from '@structural-climbing/ui'
import {
  GymSettingsForm,
  type GymSettings,
  type GymSettingsFormHandle,
} from '../features/settings/GymSettingsForm'
import { LegalDocumentsSection } from '../features/settings/LegalDocumentsSection'
import { PaymentsSection } from '../features/settings/PaymentsSection'
import { StructuralBillingSection } from '../features/settings/StructuralBillingSection'
import { PageTabs } from '../components/PageTabs'
import { PageHeader } from '../components/PageHeader'

const TAB_KEYS = ['general', 'branding', 'payments', 'legal', 'billing'] as const
type SettingsTab = (typeof TAB_KEYS)[number]

export function SettingsPage() {
  const gymId = useGymId()
  const { getAccessTokenSilently } = useAuth()
  const { t, i18n } = useTranslation()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const [settings, setSettings] = useState<GymSettings | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  // The tab lives in the query string, so a link can point straight at a
  // section and a refresh comes back to it. Stripe's onboarding return_url
  // relies on this (workers/api/src/handlers/payments.ts): without it an owner
  // who had just finished connecting landed on General and had to go looking
  // for the tab they came back to.
  const [tab, setTab] = useTabParam('tab', TAB_KEYS, 'general')
  const formRef = useRef<GymSettingsFormHandle>(null)

  useEffect(() => {
    if (!gymId) return
    setLoading(true)
    getAccessTokenSilently()
      .then(async (token) => {
        const res = await fetch(`${apiUrl}/gym-settings`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (!res.ok) {
          setError(t('settings.errorLoad'))
          return
        }
        setSettings((await res.json()) as GymSettings)
      })
      .catch(() => setError(t('settings.errorLoad')))
      .finally(() => setLoading(false))
  }, [gymId, apiUrl, getAccessTokenSilently, t])

  async function handleSave(updated: GymSettings) {
    setSaving(true)
    setSaveError(null)
    setSaved(false)
    const token = await getAccessTokenSilently()
    const res = await fetch(`${apiUrl}/gym-settings`, {
      method: 'PATCH',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(updated),
    })
    if (!res.ok) {
      setSaveError(t('settings.errorSave'))
    } else {
      i18n.changeLanguage(updated.default_language ?? 'ro')
      setSettings(updated)
      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
    }
    setSaving(false)
  }

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center p-8">
        <Spinner size="lg" color="primary" />
      </div>
    )
  }

  if (error || !settings) {
    return (
      <div className="p-4 sm:p-8">
        <p className="text-danger">{error ?? t('settings.gymNotFound')}</p>
      </div>
    )
  }

  const TAB_LABELS: Record<SettingsTab, string> = {
    general: t('settings.tabGeneral'),
    branding: t('settings.tabBranding'),
    payments: t('settings.tabPayments'),
    legal: t('settings.tabLegal'),
    billing: t('settings.tabBilling'),
  }

  return (
    <div className="p-4 sm:p-8">
      <PageHeader
        title={t('settings.title')}
        subtitle={t('settings.subtitle')}
        actions={
          <>
            {tab !== 'legal' && tab !== 'payments' && (
              <Button
                color="primary"
                isLoading={saving}
                isDisabled={saving}
                onClick={() => formRef.current?.submit()}
              >
                {t('settings.saveChanges')}
              </Button>
            )}
          </>
        }
      />

      <PageTabs
        ariaLabel={t('settings.title')}
        selectedKey={tab}
        onSelectionChange={setTab}
        items={TAB_KEYS.map((key) => ({ key, label: TAB_LABELS[key] }))}
      />

      {saveError && <p className="mb-4 text-sm text-danger">{saveError}</p>}
      {saved && <p className="mb-4 text-sm text-success">{t('settings.saved')}</p>}

      <div style={{ display: tab === 'legal' || tab === 'payments' ? 'none' : undefined }}>
        <GymSettingsForm
          ref={formRef}
          gymId={gymId!}
          initialValues={settings}
          onSave={handleSave}
          activeTab={tab as 'general' | 'branding' | 'billing'}
        />
      </div>

      {tab === 'billing' && (
        <div className="mt-6">
          <StructuralBillingSection />
        </div>
      )}

      {tab === 'payments' && <PaymentsSection branding={settings} />}
      {tab === 'legal' && <LegalDocumentsSection />}
    </div>
  )
}
